import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { GridOptions } from 'ag-grid';
import { SweetAlertHelper } from 'app/core/helpers/sweet-alert-helper.service';
import { CustomDate } from 'app/core/helpers/format-date-helper';
import { RiderInterface } from 'app/core/interfaces/rider.interface';
import { ResponseInterface } from 'app/core/interfaces/response.interface';
import { RiderService } from 'app/core/services/rider.service';
import { T2LoadingService } from 'app/trip2-theme/components/t2-loading/t2-loading.service';
import { ViewAttachmentFileComponent } from '../../../A2-utils/grid-components/view-attachment-file/view-attachment-file.component';
import { IRiderSignature } from 'app/core/interfaces/rider-signature.interface';
import { Subscription } from 'rxjs/Subscription';
import { T2CardComponent } from 'app/trip2-theme/components/t2-card/t2-card.component';
import { IMyOptions } from 'mydatepicker';

@Component({
  selector: 'app-rider-signatures',
  templateUrl: './rider-signatures.component.html',
  styleUrls: ['./rider-signatures.component.scss'],
})
export class RiderSignaturesComponent implements OnInit, OnDestroy {
  @ViewChild('signatureModal') signatureModal: T2CardComponent;
  protected grid: GridOptions;
  protected dataGrid: IRiderSignature[] = [];
  protected includeInactive = false;
  protected selectedSignature: IRiderSignature;
  protected expirationDateValue = '';
  protected expirationDatePicker: any;
  protected myDatePickerOptions: IMyOptions = {
    showSelectorArrow: false,
    dateFormat: 'mm/dd/yyyy',
    editableDateField: false,
    openSelectorOnInputClick: true,
  };

  private riderId = 0;
  private subscriptions: Subscription[] = [];

  constructor(
    private riderService: RiderService,
    private sweetAlertHelper: SweetAlertHelper,
    private loadingService: T2LoadingService,
  ) {
    this.initGrid();
  }

  ngOnInit(): void {
    this.subscribeToRider();
  }

  initGrid() {
    this.grid = <GridOptions>{
      columnDefs: this.columDefs(),
      enableColResize: true,
      enableFilter: true,
      enableSorting: true,
      onCellDoubleClicked: (params) => {
        if (params.colDef && params.colDef.field === 'attachment') {
          return;
        }
        this.openSignatureModal(params.data as IRiderSignature);
      },
    };
    this.grid.onGridReady = () => {
      if (this.grid && this.grid.api) {
        this.grid.api.sizeColumnsToFit();
      }
    };
  }

  protected onIncludeInactiveChange(checked: boolean): void {
    this.includeInactive = checked;
    if (this.hasValidRiderId) {
      this.loadSignatures();
    }
  }

  private subscribeToRider(): void {
    this.subscriptions.push(
      this.riderService.riderInformation$.subscribe({
        next: (rider: RiderInterface) => {
          if (rider && rider.riderid) {
            const riderId = rider && rider.riderid ? rider.riderid : null;
            this.riderId = riderId;
            if (this.hasValidRiderId) {
              this.loadSignatures();
            } else {
              this.dataGrid = [];
            }
          }
        },
      }),
    );
  }

  private get hasValidRiderId(): boolean {
    return !!this.riderId;
  }

  private loadSignatures(): void {
    if (!this.hasValidRiderId) {
      this.dataGrid = [];
      return;
    }

    this.loadingService.show('Loading signatures...');

    this.subscriptions.push(
      this.riderService
        .getSignaturesByRiderId(this.riderId, this.includeInactive)
        .finally(() => this.loadingService.hiden())
        .subscribe({
          next: (data: IRiderSignature[]) => {
            if (data && data.length) {
              this.dataGrid = data;
            } else {
              this.dataGrid = [];
            }
          },
          error: (error: any) => {
            this.dataGrid = [];
            this.sweetAlertHelper.captureException(error);
          },
        }),
    );
  }

  protected closeSignatureModal(): void {
    if (this.signatureModal) {
      this.signatureModal.close();
    }
    this.resetModalData();
  }

  protected onSignatureModalAction(modalEvent: any): void {
    if (modalEvent && !modalEvent.isOpen) {
      this.resetModalData();
    }
  }

  protected get canEditSelectedSignature(): boolean {
    return this.isSignatureEditable(this.selectedSignature);
  }

  protected saveExpirationDate(): void {
    if (!this.selectedSignature) {
      return;
    }

    if (!this.canEditSelectedSignature) {
      return;
    }

    const startDateValue = CustomDate.formatJs(
      this.selectedSignature.startdate,
      'yyyy-MM-dd',
    );
    if (
      startDateValue &&
      this.expirationDateValue &&
      this.expirationDateValue < startDateValue
    ) {
      this.sweetAlertHelper.createCustomAlert({
        title: 'Expiration date cannot be before start date',
        type: 'warning',
      });
      return;
    }

    const signatureToUpdate: IRiderSignature = Object.assign(
      {},
      this.selectedSignature,
      {
        expirationdate: this.expirationDateValue,
        signatureid: this.selectedSignature.ridersignatureid,
      },
    );

    this.loadingService.show('Updating signature...');

    this.subscriptions.push(
      this.riderService
        .updateRiderSignatureExpiration(signatureToUpdate)
        .finally(() => this.loadingService.hiden())
        .subscribe({
          next: (response: any) => {
            if (response) {
              this.closeSignatureModal();
              this.loadSignatures();
            }
          },
          error: (error: any) => {
            this.sweetAlertHelper.captureException(error);
          },
        }),
    );
  }

  protected resetSignature(): void {
    if (!this.selectedSignature || !this.canEditSelectedSignature) {
      return;
    }

    const signatureId = this.selectedSignature.ridersignatureid;
    if (!signatureId) {
      return;
    }

    this.sweetAlertHelper
      .createCustomAlert({
        title: 'Reset Signature',
        text:
         'Are you sure you want to reset this signature? Once it is reseted, the patient will need to sign again in the Driver app.',
        type: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, reset signature',
      })
      .then((result: any) => {
        if (!result || !result.value) {
          return;
        }
        this.loadingService.show('Resetting signature...');
        this.subscriptions.push(
          this.riderService
            .resetSignatureById(signatureId)
            .finally(() => this.loadingService.hiden())
            .subscribe({
              next: (response: any) => {
                if (response) {
                  this.closeSignatureModal();
                  this.loadSignatures();
                }
              },
              error: (error: any) => {
                this.sweetAlertHelper.captureException(error);
              },
            }),
        );
      });
  }

  protected onExpirationDateChanged(event: any): void {
    if (event && event.date) {
      this.expirationDateValue = CustomDate.formatFromObject(event.date) || '';
      return;
    }

    this.expirationDateValue = '';
  }

  private openSignatureModal(signature: IRiderSignature): void {
    if (!signature) {
      return;
    }

    this.selectedSignature = Object.assign({}, signature);
    this.expirationDateValue =
      CustomDate.formatJs(signature.expirationdate, 'yyyy-MM-dd') || '';
    this.expirationDatePicker = CustomDate.formatToDatePickerModel(
      signature.expirationdate,
    );
    if (this.signatureModal) {
      this.signatureModal.open();
    }
  }

  private isSignatureEditable(signature: IRiderSignature): boolean {
    if (!signature) {
      return false;
    }

    const expirationDateValue = CustomDate.formatJs(
      signature.expirationdate,
      'yyyy-MM-dd',
    );

    if (!expirationDateValue) {
      return true;
    }

    const todayDateValue = CustomDate.formatJs(
      new Date().toDateString(),
      'yyyy-MM-dd',
    );

    if (!todayDateValue) {
      return false;
    }

    return expirationDateValue >= todayDateValue;
  }

  private resetModalData(): void {
    this.selectedSignature = null;
    this.expirationDateValue = '';
    this.expirationDatePicker = null;
  }

  private columDefs() {
    return [
      {
        headerName: 'Start Date',
        field: 'startdateformatted',
        width: 310,
      },
      {
        headerName: 'Expiration Date',
        field: 'expirationdateformatted',
        width: 310,
      },
      {
        headerName: 'Attachment',
        field: 'attachment',
        cellRendererFramework: ViewAttachmentFileComponent,
        width: 200,
      },
    ];
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => {
      if (subscription && !subscription.closed) {
        subscription.unsubscribe();
      }
    });
  }
}



// WEBPACK FOOTER //
// ./src/app/pages/rider/components/rider-signatures/rider-signatures.component.ts